'use client';

import {useEffect} from 'react';

import type {SideMenuDrawerProps as Props} from './typings';

import {StyledDrawerBackdrop, StyledSideMenuDrawer} from './styles';

/**
 * @description When we toggle body `{ overflow: hidden; }` and `overflow: auto;`,
 * the scrollbar appears or disappears, which can cause the layout shift
 * @return {number} width of the scrollbar in pixels, `0` if no scrollbar
 */
const getScrollbarWidth = () => {
  const {scrollHeight, clientHeight} = document.documentElement;
  const hasVerticalScroll = scrollHeight > clientHeight;

  if (!hasVerticalScroll) return 0;

  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  document.body.appendChild(outer);

  const inner = document.createElement('div');
  outer.appendChild(inner);

  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  /**
   * Cleanup: remove temporary elements
   */
  outer.parentNode?.removeChild(outer);

  return scrollbarWidth;
};

/**
 * @description Hook to prevent body scroll when the drawer is open
 * @param {{isOpen: boolean}} params
 * @return {void} void/effect-only hook
 */
const usePreventOuterScroll = ({isOpen}: Pick<Props, 'isOpen'>) => {
  useEffect(() => {
    if (isOpen) {
      const scrollWidth = getScrollbarWidth();
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollWidth}px`;
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.paddingRight = '';
    }
  }, [isOpen]);
};

const SideMenuDrawer = ({children, isOpen, onClose}: Props): JSX.Element | null => {
  usePreventOuterScroll({isOpen});

  return (
    <>
      <StyledDrawerBackdrop $isOpen={isOpen} onClick={onClose} />
      <StyledSideMenuDrawer $isOpen={isOpen}>{children}</StyledSideMenuDrawer>
    </>
  );
};

export default SideMenuDrawer;
